import React, { useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Heading, Text } from '@weave/design-system';
import {
  UnifyLocationDetails,
  UnifyLocationNotes,
  UnifyLocationRow,
} from '../unify-locations-table/unify-locations-table.types';
import { UnifyLocationsTable } from '../unify-locations-table/unify-locations-table';
import { UnifyHandoffFooter } from '../unify-handoff-footer/unify-handoff-footer';
import { useUnifyHandoffContext } from '../../unify-handoff-context';

export interface UnifySelectLocationsProps {
  locations?: UnifyLocationRow[];
  selectedLocationIds?: string[];
  locationDetailsMap?: Record<string, UnifyLocationDetails>;
  showValidationErrors?: boolean;
  canProceedToNext?: boolean;
  hasMinSelections?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  onPocChange?: (locationId: string, pocEmail: string) => void;
  onNotesChange?: (locationId: string, notes: UnifyLocationNotes) => void;
  onNext?: () => void;
  onCancel?: () => void;
  onSaveAndClose?: () => void;
  isSaving?: boolean;
  maxSelections?: number;
}

export const UnifySelectLocations: React.FC<UnifySelectLocationsProps> = ({
  locations: propLocations,
  selectedLocationIds: propSelectedLocationIds,
  locationDetailsMap: propLocationDetailsMap,
  showValidationErrors: propShowValidationErrors,
  canProceedToNext: propCanProceedToNext,
  hasMinSelections: propHasMinSelections,
  onSelectionChange: propOnSelectionChange,
  onPocChange: propOnPocChange,
  onNotesChange: propOnNotesChange,
  onNext: propOnNext,
  onCancel: propOnCancel,
  onSaveAndClose: propOnSaveAndClose,
  isSaving: propIsSaving,
  maxSelections: propMaxSelections,
}) => {
  const context = useUnifyHandoffContext();

  const locations = propLocations ?? context.locations;
  const selectedLocationIds = propSelectedLocationIds ?? context.selectedLocationIds;
  const locationDetailsMap = propLocationDetailsMap ?? context.locationDetailsMap;
  const showValidationErrors = propShowValidationErrors ?? context.showValidationErrors;
  const canProceedToNext = propCanProceedToNext ?? context.canProceedToEvaluation;
  const hasMinSelections = propHasMinSelections ?? context.hasMinSelections;
  const maxSelections = propMaxSelections ?? context.maxSelections;

  const onSelectionChange = propOnSelectionChange ?? context.handleSelectionChange;
  const onPocChange = propOnPocChange ?? context.handlePocChange;
  const onNotesChange = propOnNotesChange ?? context.handleNotesChange;

  return (
    <div css={styles.container}>
      <Heading level={2} css={styles.heading}>
        Select Locations
      </Heading>
      <Text as="div" size="medium" color="light">
        Choose which locations you want to create handoff information for below.
      </Text>
      <UnifyLocationsTable
        locations={locations}
        mode="selection"
        selectedLocationIds={selectedLocationIds}
        onSelectionChange={onSelectionChange}
        maxSelections={maxSelections}
        locationDetailsMap={locationDetailsMap}
        onPocChange={onPocChange}
        onNotesChange={onNotesChange}
        showValidationErrors={showValidationErrors}
      />

      <UnifyHandoffFooter
        step="select-locations"
        canProceed={canProceedToNext}
        hasMinSelections={hasMinSelections}
        onNext={propOnNext}
        onCancel={propOnCancel}
        onSaveAndClose={propOnSaveAndClose}
        isSaving={propIsSaving}
      />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  heading: css`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
  `,
  selectAllContainer: css`
    display: flex;
    align-items: center;
    margin: ${theme.spacing(0.5, 0)};
  `,
};
